// Global realtime toast surfacing.
//
// Mounted once inside the Toaster (which itself wraps the app shell) — listens
// to org-wide topics and converts a small allowlist of events into toasts
// scoped to the current user's role:
//
//   producer  -> TakeUploaded, BatchSubmitted, PickupRequested
//   supervisor -> BatchSubmitted, CommentCreated (mentioning them)
//   actor     -> PickupRequested (their cue), TakeApproved (theirs)
//
// Anything not in the per-role allowlist is silently dropped — the channel
// invalidation in the per-route hooks already keeps the cache fresh; this
// component is only about surfacing user-visible noise.

import { useToast } from '@ohear/ui'
import { type Role, useAuth } from '../../lib/auth'
import type { RealtimeEvent, RealtimeEventType } from '../../lib/realtime/events'
import { useRealtimeChannel } from '../../lib/realtime/use-channel'
import { useOrgId } from './use-org-id'

/**
 * Per-role allowlist of event types that should pop a toast. The body of the
 * toast is built from the event's `payload` — keep these blurbs short, the
 * notification bell + dedicated pages own the long-form view.
 */
const ROLE_ALLOWLIST: Record<Role, ReadonlySet<RealtimeEventType>> = {
  owner: new Set<RealtimeEventType>(['TakeUploaded', 'BatchSubmitted', 'PickupRequested']),
  member: new Set<RealtimeEventType>(['TakeUploaded', 'BatchSubmitted', 'PickupRequested']),
  supervisor: new Set<RealtimeEventType>(['BatchSubmitted', 'CommentCreated']),
  actor: new Set<RealtimeEventType>(['PickupRequested', 'TakeApproved']),
}

interface ToastShape {
  title: string
  description?: string
  severity: 'info' | 'success' | 'warn' | 'error'
}

function describeEvent(event: RealtimeEvent, role: Role): ToastShape | null {
  const allowed = ROLE_ALLOWLIST[role]
  if (!allowed.has(event.event_type as RealtimeEventType)) return null

  const payload = event.payload ?? {}
  const character = pickString(payload, 'character_code', 'character_name')
  const cue = pickString(payload, 'cue_number', 'cue_id')
  const project = pickString(payload, 'project_name', 'project_id')

  switch (event.event_type) {
    case 'TakeUploaded':
      return {
        severity: 'info',
        title: 'New take uploaded',
        description: joinDesc([character, cue && `cue ${cue}`, project]),
      }
    case 'TakeApproved':
      return {
        severity: 'success',
        title: 'Take approved',
        description: joinDesc([character, cue && `cue ${cue}`]),
      }
    case 'BatchSubmitted':
      return {
        severity: 'info',
        title: 'Batch ready for review',
        description: joinDesc([character, project]),
      }
    case 'PickupRequested':
      return {
        severity: 'warn',
        title: 'Pickup requested',
        description: joinDesc([character, cue && `cue ${cue}`]),
      }
    case 'CommentCreated': {
      // Supervisor-only mention filtering: the event payload sets
      // `mentioned_user_ids` when the comment @-mentions specific users.
      // Untargeted comments are skipped to avoid noise.
      const mentioned = payload['mentioned_user_ids']
      if (!Array.isArray(mentioned) || mentioned.length === 0) return null
      return {
        severity: 'info',
        title: 'You were mentioned',
        description: joinDesc([character, cue && `cue ${cue}`]),
      }
    }
    default:
      return null
  }
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | null {
  for (const k of keys) {
    const v = obj[k]
    if (typeof v === 'string' && v.length > 0) return v
    if (typeof v === 'number') return String(v)
  }
  return null
}

function joinDesc(parts: Array<string | null | false>): string | undefined {
  const filtered = parts.filter((p): p is string => Boolean(p))
  if (filtered.length === 0) return undefined
  return filtered.join(' · ')
}

/**
 * Mount once at app shell level (inside <Toaster>). Subscribes to the four
 * org topics and pushes role-relevant events into the toast queue.
 */
export function RealtimeToastListener() {
  const { role, session } = useAuth()
  const orgId = useOrgId()
  const toast = useToast()

  // Filter helper that toasts when allowed and the event passes the per-role
  // additional filtering (e.g. CommentCreated only when @-mentioned).
  const handle = (event: RealtimeEvent) => {
    if (!role) return
    if (event.event_type === 'CommentCreated' && role === 'supervisor') {
      const mentioned = event.payload?.['mentioned_user_ids']
      const userId = session?.user.id
      if (!userId || !Array.isArray(mentioned) || !mentioned.includes(userId)) return
    }
    const desc = describeEvent(event, role)
    if (!desc) return
    toast.show(desc)
  }

  useRealtimeChannel({ orgId, topic: 'takes', onEvent: handle })
  useRealtimeChannel({ orgId, topic: 'comments', onEvent: handle })
  useRealtimeChannel({ orgId, topic: 'project_progress', onEvent: handle })
  useRealtimeChannel({ orgId, topic: 'notifications', onEvent: handle })

  return null
}
